import ActionModel from '../models/ActionModel.js'
import {getColoredUseLine, getColoredNameById} from '../../helpers/rollout-message-text-helpers.js'

const SpecialActionModel = {
  ...ActionModel,
  ppCost: 5, //TODO: actual economy
};

//Special
export default {

  //ForEach
  "special_foreach": {
    ...SpecialActionModel,
    name: "ForEach",
    category: "offense",
    ppCost: 4,
    affectTargetHpPointsInRepetitions: {min: 2, max: 4, affectTargetHpPoints: -5}, //doesn't include attack stat
    description: "Delivers pain in 2 to 4 iterations.",
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0];
      const count = descriptionObject.description.targetsDamageTracker[targetModel.id].length;

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),

        {
          type: "animation",
          animationKey: "AnimationForEach",
          animationData: {
            casterId: casterModel.id,
            targetIds: targetModels.map(t => t.id),
            targetsDamageTracker: descriptionObject.description.targetsDamageTracker
          },
        },

        { //Play the state change!
          type: "stateChange",
          newCombatantState: descriptionObject.state,
          description: descriptionObject.description || {}
        },

        {
          type: "message",
          content: [
            `It hit ${count} times!`,
          ]
        }
      ]
    },
  },



  //Slice
  "special_slice": {
    ...SpecialActionModel,
    name: "Slice",
    category: "offense",
    description: "Offensive attack which might decrease opponent defense.",
    affectTargetHpPoints: -5,
    ppCost: 4,
    affectTargetDefenseModifier: {percentChance: 40, value: -3}, //X% Chance that this will happen
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0];

      //Add possible "target's defense was lowered" line.
      const wasDefenseChanged = descriptionObject.description.defenseChange &&
        descriptionObject.description.defenseChange[targetModel.id].defenseChange !== 0;

      let defenseSteps = (wasDefenseChanged) ? [
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            `'s defense is lowered!`,
          ]
        }
      ] : [];

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),

        {
          type: "animation",
          animationKey: "AnimationSlice",
          animationData: {
            casterId: casterModel.id,
            targetIds: targetModels.map(t => t.id)
          },
        },
        { //Play the state change!
          type: "stateChange",
          newCombatantState: descriptionObject.state,
          description: descriptionObject.description || {}
        },
        ...defenseSteps
      ]
    },
  },

  //Clean (Single Ally)
  "special_clean": {
    ...SpecialActionModel,
    name: "Clean",
    category: "healing",
    description: "Clear your own status back to normal.",
    ppCost: 5,
    targetType: "only-me",
    affectTargetStatus: ["*", "normal"],
    canBeReplaced: false,

    shouldFail: function (actionModel, casterModel, targetModels) {
      if (!targetModels.length) {
        return true; //should fail if no targets (see "no targets" note in submit-action.js)
      }
      return (targetModels[0].status === "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status === "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} has no status conditions!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        {
          type: "message",
          content: [
            getColoredNameById(targetModels[0].id),
            `'s status is cleared!`,
          ]
        }
      ]
    },
  },



  //DDoS
  "special_ddos": {
    ...SpecialActionModel,
    name: "DDoS",
    category: "cursing",
    description: "Causes an opponent to lag for 2 to 4 turns.",
    targetType: "single-opponent",
    affectTargetStatus: ["normal", "lag"],
    ppCost: 6,

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` is lagging out!`,
          ]
        }
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 affects single-opponent
      return [
        {
          //Target recovers lagging in 2 to 5 turns
          action: {
            id: "natural_recover-lag",
            casterId: targetModel.id,
            targetIds: [targetModel.id]
          },
          turnRange: [2, 4]
        }
      ]
    }
  },

  //Headphones
  "special_headphones": {
    ...SpecialActionModel,
    name: "Headphones",
    category: "boosting",
    description: "Chill back to maxed accuracy and increased defense.",

    targetType: "single-ally",
    affectTargetStatus: ["normal", "focus"],
    ppCost: 3,
    canMiss: false,

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target

      //Change text based on giving out or using for myself. (Caster targets him/herself)
      const actionLine  = targetModel.id === casterModel.id
        ? " puts on Headphones!"
        : ` hands Headphones to ${targetModel.name}!`;

      return [
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            actionLine
          ]
        },
        {
          type: "animation",
          animationKey: "AnimationHeadphones",
          animationData: {
            casterId: casterModel.id,
            targetIds: targetModels.map(t => t.id)
          },
        },
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` enters a state of Focus!`,
          ]
        }
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 affects single-opponent
      return [
        {
          //Target recovers focus in 2 to 5 turns
          action: {
            id: "natural_recover-focus",
            casterId: targetModel.id,
            targetIds: [targetModel.id]
          },
          turnRange: [2, 5]
          //turnRange: [1,1]
        }
      ]
    }
  },

  //Troll
  "special_troll": {
    ...SpecialActionModel,
    name: "Troll",
    category: "boosting",
    description: "Become furious. Increases attack but reduces accuracy.",
    targetType: "single-ally",
    affectTargetStatus: ["normal", "fury"],
    ppCost: 3,
    canMiss: false,

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target
      return [
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` is trolling up a storm!`,
          ]
        },
        {
          type: "animation",
          animationKey: "AnimationTroll",
          animationData: {
            casterId: casterModel.id,
            targetIds: targetModels.map(t => t.id)
          },
        },
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` is enraged in Fury!`,
          ]
        }
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 affects single-opponent
      return [
        {
          //Target recovers focus in 2 to 5 turns
          action: {
            id: "natural_recover-fury",
            casterId: targetModel.id,
            targetIds: [targetModel.id]
          },
          turnRange: [2, 5]
        }
      ]
    }
  },

  //Deadline
  "special_deadline": {
    ...SpecialActionModel,
    name: "Deadline",
    category: "boosting",
    description: "Gain an extra action submission for 2 to 3 turns.",

    targetType: "single-ally",
    affectTargetStatus: ["normal", "deadline"],
    ppCost: 6,
    canMiss: false, //It feels weird when a deadline misses. How on earth do you "miss" that?

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target
      return [
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` establishes a Deadline!`,
          ]
        },
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` is extra productive!`,
          ]
        }
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 affects single-opponent
      return [
        {
          //Target recovers deadline in 2 to 3 turns
          action: {
            id: "natural_recover-deadline",
            casterId: targetModel.id,
            targetIds: [targetModel.id]
          },
          turnRange: [2,3] // 2,3
        }
      ]
    }
  },


  //Garbage Jammer
  "special_garbage-jammer": {
    ...SpecialActionModel,
    name: "Garbage Jammer",
    category: "cursing",
    description: "Causes an opponent to leak HP after each turn.",
    targetType: "single-opponent",
    affectTargetStatus: ["normal", "memory-leak"],
    ppCost: 6,

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` has a memory leak!`,
          ]
        }
      ]
    }
  },

  //Thrash
  "special_thrash": {
    ...SpecialActionModel,
    name: "Thrash",
    category: "cursing",
    description: "Overheat an opponent's laptop to a flaming good-bye.",
    targetType: "single-opponent",
    affectTargetStatus: ["normal", "memory-leak"],
    ppCost: 8,

    shouldFail: function (actionModel, casterModel, targetModels) {
      return (targetModels[0].status !== "normal");
    },
    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (targetModel.status !== "normal") {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} already has a status!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            ` has a memory leak!`,
          ]
        },
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            `'s laptop is getting uncomfortably warm...`,
          ]
        }
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 affects single-opponent
      return [
        {
          action: {
            id: "natural_memory-leak-to-fire",
            casterId: targetModel.id,
            targetIds: [targetModel.id]
          },
          turnRange: [2, 2] //escalate this memory-leak to a fire status
        }
      ]
    }
  },

  //Curl
  "special_curl": {
    ...SpecialActionModel,
    name: "Curl",
    category: "theft",
    description: "Steal 1 random item from the enemy team.",
    targetType: "opponent-team",

    theftQuantity: 1,
    ppCost: 8,

    shouldFail: function (actionModel, casterModel, targetModels, descriptionObject) {

      let targetTeamItems = targetModels.map(targetModel => {
        return targetModel.items
      }).reduce((a, b) => {
        return [...a, ...b]
      });
      return targetTeamItems.length === 0; //Return TRUE if this team has zero items.
    },

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        getColoredUseLine(casterModel.id, actionModel.name),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` stole ${descriptionObject.description.stolenItemsFriendlyText}!`,
          ]
        }
      ]
    },
  },
  //Curl Mk II -- copied from Curl. Can we clean that up? Might not matter
  "special_curl-mk2": {
    ...SpecialActionModel,

    name: "Curl Mk II",
    category: "theft",
    description: "Steal 2 items from the enemy team.",
    targetType: "opponent-team",
    theftQuantity: 2,
    ppCost: 10,

    shouldFail: function (actionModel, casterModel, targetModels, descriptionObject) {

      let targetTeamItems = targetModels.map(targetModel => {
        return targetModel.items
      }).reduce((a, b) => {
        return [...a, ...b]
      });
      return targetTeamItems.length === 0; //Return TRUE if this team has zero items.
    },

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        getColoredUseLine(casterModel.id, actionModel.name),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` stole ${descriptionObject.description.stolenItemsFriendlyText}!`,
          ]
        }
      ]
    },
  },

  "special_copy-paste": {
    ...SpecialActionModel,
    name: "Copy/Paste",
    category: "theft",
    description: "Steal an enemy item and immediately use it.",
    targetType: "opponent-team",

    theftQuantity: 1,
    stealAndUseItem: true,
    ppCost: 10,

    shouldFail: function (actionModel, casterModel, targetModels) {
      let targetTeamItems = targetModels.map(targetModel => {
        return targetModel.items
      }).reduce((a, b) => {
        return [...a, ...b]
      });
      return targetTeamItems.length === 0; //Return TRUE if this team has zero items.
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        getColoredUseLine(casterModel.id, actionModel.name),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` stole ${descriptionObject.description.stolenItemsFriendlyText}!`,
          ]
        }
      ]
    },
  },

  //SQL Injection
  "special_sql-injection": {
    ...SpecialActionModel,

    name: "SQL Injection",
    category: "theft",
    description: "Delete one random enemy item.",

    targetType: "opponent-team",
    targetCanBeDead: true,
    ignoreTargetDieResult: true, //Don't show any "he died" messaging. He might already be dead, and this can't hurt ya.

    deleteItemQuantity: 1,
    ppCost: 6,

    shouldFail: function (actionModel, casterModel, targetModels, descriptionObject) {
      let targetTeamItems = targetModels.map(targetModel => {
        return targetModel.items
      }).reduce((a, b) => {
        return [...a, ...b]
      });
      return targetTeamItems.length === 0; //Return TRUE if this team has zero items.
    },

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` deleted ${descriptionObject.description.deletedItemsFriendlyText} from the enemy team!`,
          ]
        }
      ]
    },
  },



  //Firewall
  "special_firewall": {
    ...SpecialActionModel,
    name: "Firewall",
    category: "boosting",
    description: "Increases team defense for the rest of the battle.",
    targetType: "ally-team",
    canMiss: false,
    affectTargetDefenseModifier: 3,
    ppCost: 7,

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      // console.log(descriptionObject);
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            `'s team defense increases!`
          ]
        }
      ]
    },
  },

  //Firewall
  "special_scrum": {
    ...SpecialActionModel,
    name: "Scrum",
    category: "boosting",
    description: "Increases team attack for the rest of the battle.",
    targetType: "ally-team",
    canMiss: false,
    affectTargetAttackModifier: 3,
    ppCost: 7,

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      // console.log(descriptionObject)
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            `'s team attack increases!`
          ]
        }
      ]
    },
  },

  //Stash //TODO: UNSURE IF THIS WILL BE AVAILABLE (Why not? It's not an ult.)
  "special_stash": {
    ...SpecialActionModel,
    name: "Stash",
    category: "cursing",
    description: "Destroy an opponent's Commit.",
    canMiss: false,
    clearTargetCommittedData: true,
    ppCost: 5,

    getValidity(actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      if (!targetModel.committedTurnData) {
        return {
          isValid: false,
          invalidReasons: [`${targetModel.name} doesn't have a Commit!`]
        }
      }
      return {
        isValid: true,
        invalidReasons: []
      };
    },

    shouldFail: function (actionModel, casterModel, targetModels) {
      const targetModel = targetModels[0];
      return (!targetModel.committedTurnData)
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0];
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        //animation
        {
          type: "message",
          content: [
            `${casterModel.name} destroyed ${targetModel.name}'s Commit!`,
          ]
        }
      ]
    }
  },

  //Promise
  "special_promise": {
    ...SpecialActionModel,
    name: "Promise",
    category: "offense",
    description: "Send a powerful blast in the air to return later.",
    targetType: "opponent-team",
    ppCost: 8,
    canMiss: false,

    //affectTargetHpPoints: -1, //Playing with not having Promise do any damage up front //UPDATE: this made AI never consider Promise. Might want to do a backdoor solution if you want 0 dmg upfront
    //Going back to 0 damage
    affectTargetHpPoints: 0,

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            ` sends a Promise into the air!`,
          ]
        },
        //animation
      ]
    },
    getFollowupActions(actionModel, casterModel, targetModels, descriptionObject) {
      return [
        {
          //Create the followup blow
          action: {
            id: "special_promise-resolve",
            casterId: casterModel.id,
            targetIds: targetModels.map(targetModel => targetModel.id)
          },
          turnRange: [2,3]
        }
      ]
    }
  },
  "special_promise-resolve": {
    ...SpecialActionModel,
    ignoreCasterDieResult: true, //Do not say the "Caster is out of hp" message for the caster of this msg
    name: "(Resolving Promise)",
    description: "", //This description is not user facing.
    //targetType: "opponent-team", //This isn't really needed because targets are provided only by the FOLLOWUP action
    selectRandomTargetForSingleHit: true, //This flag is intended to take an array of targets and only hit 1.
    canMiss: false, //seems unfair that this could miss
    isImmuneFromLag: true, //do not allow this move to get lost in lag
    affectTargetHpPoints: -9,
    ppCost: 0,

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      return [
        //animation
        {
          type: "message",
          content: [
            getColoredNameById(casterModel.id),
            `'s Promise resolves!`,
          ]
        }
      ]
    },
  },

  //Bug
  "special_bug": {
    ...SpecialActionModel,
    name: "Bug",
    category: "theft",
    description: "Sabotages 1 random item on the enemy team.",
    targetType: "opponent-team",
    sabotageItemQuantity: 1,
    ppCost: 6,

    //Check that Target Team actually has some items to bug
    shouldFail: function (actionModel, casterModel, targetModels) {
      const targetTeamNonBuggedItems = targetModels.map(targetModel => {
        return targetModel.items
      }).reduce((a, b) => [...a, ...b]).filter(item => !item.isBugged);
      return targetTeamNonBuggedItems.length === 0; //Action fails if target team has no non-bugged items
    },
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const firstTarget = targetModels[0];
      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        //animation
        {
          type: "message",
          content: [
            'An item on ',
            getColoredNameById(firstTarget.id),
            `'s team was bugged!`
          ]
        }
      ]
    },
  },

  //Scope Bomb
  "special_scope-bomb": {
    ...SpecialActionModel,
    name: "Scope Bomb",
    category: "offense",
    description: "Strike the opponent with a burndown surprise. Extra painful against a Deadline.",
    affectTargetHpPoints: -3,
    ppCost: 5,

    statusMultiplier: ["deadline", 1.5], //1.5x the damage
    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target

      //Potentially include this line
      const extraEffectiveLine = targetModel.status === "deadline" ? {
          type: "message",
          content: [
            `It is extra painful!`,
          ]
        } : null;

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        extraEffectiveLine, //Note: explicitly doing present tense effective line before the animation
        //...animation
      ].filter(line => Boolean(line))
    },
  },

  //Screen Crack
  "special_screen-crack": {
    ...SpecialActionModel,
    name: "Screen Crack",
    category: "cursing",
    description: "Lowers target accuracy by 5%.",
    affectTargetAccuracyModifier: -5,
    ppCost: 5,

    getSuccessStep: function (actionModel, casterModel, targetModels, descriptionObject) {
      const targetModel = targetModels[0]; //Only 1 target

      return [
        getColoredUseLine(casterModel.id, actionModel.name, ""),
        {
          type: "message",
          content: [
            getColoredNameById(targetModel.id),
            `'s accuracy was lowered!`,
          ]
        }
      ]

    },
  },

}


// WEBPACK FOOTER //
// ./src/battle/battle-data/_specials.js